import { Form, SubmissionData } from '../types'
const API_URL = process.env.REACT_APP_API_URL;

export const handleEditForm = (navigate: any, form: Form) => {
  navigate(`/form/${form.id}/edit`);
  };

export const deleteForm = async (formId: string) => {
  try {
    const response = await fetch(`${API_URL}/api/forms/${formId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete form: ${response.statusText}`);
    }

    //console.log(`Form with ID ${formId} deleted.`);
  } catch (err) {
    console.error('Error deleting form:', err);
  }
};

export const copyForm = async (form: Form) => {
  try {
    const { id, ...formDataWithoutId } = form;

    const response = await fetch(`${API_URL}/api/forms`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formDataWithoutId),
    });

    if (!response.ok) {
      throw new Error(`Failed to copy form: ${response.statusText}`);
    }

    //console.log('Form copied:', await response.json());
  } catch (err) {
    console.error('Error copying form:', err);
  }
};

export const getFormStatus = (form: Form) => {
  if (form.isComplete) return "Complete";
  if (form.submissions && form.submissions.length > 0) return "In Progress";
  return "Uncompleted Task";
};

export const formatDate = (dateString: string, timezone: string) => {
  if (!dateString) return "No end date";

  const utcDate = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  switch (timezone) {
    case "PST":
      options.timeZone = "America/Los_Angeles";
      return new Intl.DateTimeFormat("en-US", options).format(utcDate) + " PST";
    case "UTC":
      options.timeZone = "UTC";
      return new Intl.DateTimeFormat("en-US", options).format(utcDate) + " UTC";
    case "EST":
      options.timeZone = "America/New_York";
      return new Intl.DateTimeFormat("en-US", options).format(utcDate) + " EST";
    case "Local":
    default:
      options.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return new Intl.DateTimeFormat("en-US", options).format(utcDate) + " Local";
  }
};

export const handleError = (err: any, callback: (message: string) => void) => {
  console.error(err);
  if (err.response?.text) {
    callback(err.response.text);
  } else if (err.message) {
    callback(err.message);
  } else {
    callback('An unknown error occurred.');
  }
};

// Update an existing form
export const updateForm = async (formId: string, updatedForm: Form): Promise<Form | null> => {
  try {
    const response = await fetch(`${API_URL}/api/forms/${formId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedForm),
    });

    if (!response.ok) {
      throw new Error('Failed to update form');
    }

    const form = await response.json();
    return form;
  } catch (err) {
    console.error('Error updating form:', err);
    return null;
  }
};

// Create a new form
export const createForm = async (form: Form): Promise<Form | null> => {
  try {
    const response = await fetch(`${API_URL}/api/forms`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(form),
    });

    if (!response.ok) {
      throw new Error('Failed to create form');
    }

    return await response.json();
  } catch (err) {
    console.error('Error creating form:', err);
    return null;
  }
};

// Fetch all forms from the backend
export const fetchForms = async (courseGuid: string): Promise<Form[]> => {
  try {
    console.log("🔍 Fetching forms with courseGuid:", courseGuid);
    const response = await fetch(`${API_URL}/api/forms?courseGuid=${courseGuid}`, {
      credentials: 'include',
    });
    
    console.log("🔍 Response status:", response.status);
    console.log("🔍 Response headers:", Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Failed to fetch forms:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`Failed to fetch forms: ${response.status} ${response.statusText}`);
    }
    
    const forms = await response.json();
    console.log("✅ Successfully fetched forms:", forms.length);
    return forms;
  } catch (err) {
    console.error("❌ Error in fetchForms:", err);
    return [];
  }
};

// Fetch form details by ID
export const fetchFormById = async (formId: string): Promise<Form | null> => {
  try {
    const response = await fetch(`${API_URL}/api/forms/${formId}`, {
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`, // Replace with your actual token
      },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch form data.');
    }

    const form = await response.json();
    //console.log('Fetched form:', form);
    // Ensure the form has valid group structures
    if (
      !Array.isArray(form.groups) ||
      !form.groups.every((g: any) => g.id && g.name)
    ) {
      throw new Error('Invalid group structure in API response.');
    }

    return form as Form;
  } catch (error) {
    console.error('Error fetching form by ID:', error);
    return null;
  }
};

export const fetchRoster = async (courseGuid: string) => {
  const url = `localhost/api/roster?siteId=${courseGuid}`;
    //console.log(`Fetching roster from URL: ${url}`); // Log the full URL

  try {
    const response = await fetch(url, {
      credentials: "include", // Include cookies for session authentication
    });

    if (!response.ok) {
      const text = await response.text();
      console.error("Error fetching roster:", text);
      throw new Error(`Failed to fetch roster: ${response.statusText}`);
    }

    const data = await response.json();
    //console.log("Fetched roster data:", data);
    //console.log("Fetched roster data:", data.membership_collection);
    return data.membership_collection; // Adjust based on the API response structure
  } catch (error) {
    console.error("Error in fetchRoster:", error);
    throw error;
  }
};

//DELETE LATER IF UNUSED
// Submit the form with selected group
export const submitForm = async (submissionData: SubmissionData): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/api/submissions`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submissionData),
    });
    if (!response.ok) {
      throw new Error('Failed to submit form.');
    }
    //console.log('Form submitted successfully:', await response.json());
  } catch (error) {
    console.error('Error submitting form:', error);
    throw error;
  }
};

export const deleteGroupMembership = async (data: {
  formId: string;
  groupId: string;
  userId: string;
  }): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/api/submissions/membership`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
    if (!response.ok) {
      const error = await response.json();
      console.error('Delete failed:', error);
      throw new Error(error.error || 'Failed to delete group membership');
    }
      
      //console.log('Group membership deleted successfully.');
    } catch (err) {
      console.error('Error deleting group membership:', err);
      throw err;
    }
};


// Print groups helper function
export const printGroups = (form: any) => {
  if (!form) {
    alert("Form data is not loaded.");
    return;
  }

  const printContent = `
    <html>
      <head>
        <title>${form.title} - Group Assignments</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { text-align: left; color: #333; }
          h2 { color: #0d6efd; }
          .border {border: 1px solid #333; border-radius: 8px; padding: 15px;}
          .groups-container { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-start; }
          .group { margin-bottom: 30px; page-break-inside: avoid; flex: 0 0 calc(45% - 10px); min-width: 300px; }
          .group-title { font-size: 18px; font-weight: bold; font-decoration: underline; color: #333; margin-bottom: 10px; }
          .group-info { color: #666; margin-bottom: 15px; }
          .member-list { margin-left: 10px; }
          .member { margin-bottom: 5px; padding: 5px; background-color: #f9f9f9; border-radius: 3px; }
          .empty-group { font-style: italic; color: #999; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <div>
          <h1>${form.title}</h1>
          <p>Description: ${form.description}</p>
          <hr>
        </div>
        <h2>Group Assignments</h2>
        <div class="groups-container">
          ${form.groups?.map((group: any) => `
            <div class="group border">
              <u class="group-title">${group.name}</u>
              <div class="group-info">Members: ${group.members?.length || 0} / ${group.maxStudents}</div>
              <ul class="member-list">
                ${group.members && group.members.length > 0 
                  ? group.members.map((member: any) => `<li class="member">${member.userName}</li>`).join('')
                  : '<li class="empty-group">No members assigned</li>'
                }
              </ul>
            </div>
          `).join('') || ''}
        </div>
        
        <div class="footer">
          <div>Printed on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
          <div>Total Groups: ${form.groups?.length || 0}</div>
          <div>Total Members: ${form.groups?.reduce((total: number, group: any) => total + (group.members?.length || 0), 0) || 0}</div>
        </div>
      </body>
    </html>
  `;

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.open();
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.focus();
    newWindow.print();
    newWindow.close();
  }
};